import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import 'flag-icons/css/flag-icons.min.css';
import { FunctionComponent, useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { FilePond, registerPlugin } from 'react-filepond';
import { useSelector } from 'react-redux';
import { Button, CardContainer, Link, MergeLink } from 'src/components/atoms';
import {
  RequiredFeatureEnabled,
  isRequiredFeatureEnabled,
} from 'src/components/auth';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import {
  Modal,
  PageHeaderWithActions,
  SimpleTable,
  handleErrorToast,
  handleSuccessToast,
} from 'src/components/molecules';
import {
  useGetOrganisationQuery,
  useRequestMergeLinkMutation,
} from 'src/graphql';
import { useIsFeatureEnabled } from 'src/hooks';
import { CountryFlag } from 'src/utils/CountryFlag';
import { getFormattedPhoneNumber } from 'src/utils/getFormattedPhoneNumber';
import { objectEntries } from 'src/utils/objectEntries';
import { CONFIGURATIONS, LOGO, MANAGERS } from '../../../api';
import axios from '../../../axios';
import * as Types from '../../../types';
import { IMAGE_MIME_TYPES, User } from '../../../types';
import { SelectMenuItem } from '../../atomsLegacy/SelectDropdown/SelectDropdown';
import CheckboxWithLabel from '../../moleculesLegacy/CheckboxWithLabel';
import { ConfigurationsSection } from '../../organisms/Configurations/ConfigurationsSection';
import { SlackConfigurationSection } from '../../organisms/Configurations/SlackConfigurationSection/SlackConfigurationSection';
import {
  ButtonContainer,
  ConfigurationsContainer,
  FilepondContainer,
  HotlineNumbers,
  LogoImage,
  LogoImageSection,
  RoutingCheckboxContainer,
  StyledRoutingDropdown,
} from './Configurations.styles';

registerPlugin(FilePondPluginImagePreview);

type ConfigurationsProps = { slug?: string };

interface FormState {
  dirty: boolean;
  oprDefaultAssigneeEnabled: boolean;
}

interface ConfigurationsModel {
  oprDefaultAssignee?: string | null;
}

interface ConfigurationDTO {
  data: ConfigurationsModel;
  status: number;
}

interface ManagersDTO {
  data: User[];
  status: number;
}

enum ConfigurationKeys {
  OPR_DEFAULT_ASSIGNEE = 'oprDefaultAssignee',
}

export const Configurations: FunctionComponent<ConfigurationsProps> = (
  props: ConfigurationsProps,
) => {
  const [logoURL, setLogoURL] = useState('/assets/vault-logo.png');
  const [editLogo, setEditing] = useState(false);
  const [managers, setManagers] = useState<User[]>([]);
  const [formState, setFormState] = useState<FormState>({
    dirty: false,
    oprDefaultAssigneeEnabled: false,
  });
  const [configurations, setConfigurations] = useState<ConfigurationsModel>({});
  const [linkToken, setLinkToken] = useState('');
  const { mutate } = useRequestMergeLinkMutation({
    onSuccess: (data) => {
      setLinkToken(data.requestMergeLinkToken);
    },
  });

  const features = useSelector((state: Types.State) => state.features);

  const vaultTalkEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.VAULT_TALK,
  );

  const publicReportingEnabled = useIsFeatureEnabled(
    Types.FeatureFlag.PUBLIC_REPORTING,
  );

  const oprDefaultAssigneeRoutingEnabled = useIsFeatureEnabled(
    Types.FeatureFlag.OPR_DEFAULT_ASSIGNEE_ROUTING,
  );

  const routingEnabled = useIsFeatureEnabled(Types.FeatureFlag.ROUTING);

  const { data } = useGetOrganisationQuery();
  const { hotlineNumbers } = data?.organisation ?? {};

  const hotlineNumbersNodes = (hotlineNumbers || []).map((number, index) => {
    const formattedPhoneNumber = getFormattedPhoneNumber(number.phoneNumber);
    return [
      <CountryFlag
        key={index}
        country={number.country}
        flagIconCode={number.flagIconCode}
      />,
      number.defaultLanguage,
      formattedPhoneNumber,
      <CopyToClipboard key={index} text={formattedPhoneNumber}>
        <Button
          className="copy-button"
          key={index}
          variant="secondary"
          size="small"
          data-testid={'copy-text'}
          width="53px"
          onClick={() =>
            handleSuccessToast({
              toastMessage: 'Telephone number copied to clipboard!',
            })
          }
        >
          Copy
        </Button>
      </CopyToClipboard>,
    ];
  });

  const getLogo = async () => {
    if (!publicReportingEnabled) return;
    try {
      const logoResp = await axios.get(`${LOGO}/${props.slug}`);
      const logo = logoResp?.data?.data?.logo ?? '/assets/vault-logo.png';
      setLogoURL(logo);
    } catch {
      handleErrorToast({ toastMessage: 'Couldn`t fetch logo' });
    }
  };

  const getManagers = async () => {
    if (!oprDefaultAssigneeRoutingEnabled || routingEnabled) return;
    try {
      const { data } = await axios.get<ManagersDTO>(MANAGERS);
      setManagers(data.data);
    } catch {
      handleErrorToast({ toastMessage: 'Couldn`t fetch case managers' });
    }
  };

  const getConfigurations = async () => {
    if (!publicReportingEnabled) return;
    try {
      const { data } = await axios.get<ConfigurationDTO>(CONFIGURATIONS);
      setConfigurations(data.data);

      if (data.data.oprDefaultAssignee) {
        setFormState({ ...formState, ...{ oprDefaultAssigneeEnabled: true } });
      }
    } catch {
      handleErrorToast({ toastMessage: 'Couldn`t fetch OPR configurations' });
    }
  };

  const putConfigurations = async () => {
    const { oprDefaultAssignee } = configurations;
    await axios.put(CONFIGURATIONS, { oprDefaultAssignee });
    handleSuccessToast({ toastMessage: 'Configuration updated' });
  };

  const [languageModalOpen, setLanguageModalOpen] = useState(false);

  useEffect(() => {
    getLogo();
    getManagers();
    getConfigurations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRoutingEnabled = useIsFeatureEnabled(Types.FeatureFlag.ROUTING);

  return (
    <ConfigurationsContainer data-testid="configurations-page">
      <PageHeaderWithActions title="Configurations" />
      <CardContainer>
        {!isRoutingEnabled && (
          <RequiredFeatureEnabled
            requiredFeature={Types.FeatureFlag.OPR_DEFAULT_ASSIGNEE_ROUTING}
          >
            <ConfigurationsSection
              first
              subheader="Default OPR Assignee"
              left={
                managers.length !== 0 &&
                objectEntries(configurations).map((kv, i) => {
                  const [key, value] = kv;

                  if (key === ConfigurationKeys.OPR_DEFAULT_ASSIGNEE) {
                    const currentOprDefaultAssignee = managers.find(
                      (manager) => manager.id == value,
                    );

                    return (
                      <RoutingCheckboxContainer key={i}>
                        <CheckboxWithLabel
                          label="Set default Open Reporting assignee"
                          data-testid="default-opr-checkbox"
                          checked={formState.oprDefaultAssigneeEnabled}
                          onChange={(event) => {
                            setFormState({
                              ...formState,
                              ...{
                                dirty:
                                  formState.oprDefaultAssigneeEnabled !==
                                  event.currentTarget.checked,
                                oprDefaultAssigneeEnabled:
                                  event.currentTarget.checked,
                              },
                            });
                            if (!event.currentTarget.checked) {
                              setConfigurations({
                                ...configurations,
                                ...{
                                  oprDefaultAssignee: null,
                                },
                              });
                            }
                          }}
                        />

                        {formState.oprDefaultAssigneeEnabled && (
                          <StyledRoutingDropdown
                            dataQa="default-opr-select"
                            style={{
                              width: '100%',
                              minWidth: '100px',
                              marginBottom: 0,
                            }}
                            disabled={!formState.oprDefaultAssigneeEnabled}
                            placeholder="Select a Manager..."
                            defaultValue={currentOprDefaultAssignee?.id}
                            onChange={(event) => {
                              setConfigurations({
                                ...configurations,
                                ...{
                                  oprDefaultAssignee: event.target
                                    .value as string,
                                },
                              });
                              setFormState({
                                ...formState,
                                ...{
                                  dirty: true,
                                },
                              });
                            }}
                          >
                            {managers.map((manager, i) => (
                              <SelectMenuItem
                                key={i}
                                value={manager.id}
                                data-testid={manager.name}
                                selected={
                                  manager.id === currentOprDefaultAssignee?.id
                                }
                              >
                                {manager.name}
                              </SelectMenuItem>
                            ))}
                          </StyledRoutingDropdown>
                        )}
                      </RoutingCheckboxContainer>
                    );
                  }

                  return null;
                })
              }
              right={
                <ButtonContainer>
                  <Button
                    variant="primary"
                    size="medium"
                    disabled={!formState.dirty}
                    onClick={() => putConfigurations()}
                    data-testid="default-opr-save"
                    width="160px"
                  >
                    Save
                  </Button>
                </ButtonContainer>
              }
            ></ConfigurationsSection>
          </RequiredFeatureEnabled>
        )}
        <RequiredFeatureEnabled requiredFeature={Types.FeatureFlag.HRIS_SYNC}>
          <ConfigurationsSection
            first={Boolean(isRoutingEnabled)}
            subheader="HRIS Integration"
            right={
              <>
                <LogoImageSection>
                  <Button
                    variant="primary"
                    size="medium"
                    width="160px"
                    onClick={() => mutate({})}
                  >
                    Manage HRIS
                  </Button>
                </LogoImageSection>
                {linkToken ? (
                  <MergeLink
                    linkToken={linkToken}
                    onExit={() => setLinkToken('')}
                  />
                ) : (
                  <div />
                )}
              </>
            }
          />
        </RequiredFeatureEnabled>

        <SlackConfigurationSection />

        <RequiredFeatureEnabled
          requiredFeature={Types.FeatureFlag.PUBLIC_REPORTING}
        >
          <ConfigurationsSection
            subheader="Company Logo"
            description={
              'You can alter the logo that is displayed in your OpenReporting header. We highly recommend uploading your logo as it will give a better look and feel for people wishing to utilise open reporting.'
            }
            right={
              <>
                {!editLogo && (
                  <LogoImageSection>
                    <LogoImage src={logoURL} data-testid="company-logo" />
                    <Button
                      variant="primary"
                      size="medium"
                      onClick={() => setEditing(!editLogo)}
                      data-testid={'edit-logo-button'}
                      width="160px"
                    >
                      Edit logo
                    </Button>
                  </LogoImageSection>
                )}
                {editLogo && (
                  <LogoImageSection data-testid={'company-logo-section'}>
                    <FilepondContainer>
                      <FilePond
                        credits={false}
                        server={`api${LOGO}`}
                        maxFiles={1}
                        allowMultiple={false}
                        acceptedFileTypes={IMAGE_MIME_TYPES}
                        labelFileProcessingError={() => {
                          handleErrorToast({
                            toastMessage: 'Failure during logo upload',
                          });
                          return 'Upload failed';
                        }}
                        onprocessfile={() => {
                          getLogo();
                          setEditing(!editLogo);
                        }}
                        onprocessfilerevert={() => {
                          getLogo();
                          setEditing(!editLogo);
                        }}
                      />
                    </FilepondContainer>
                    <Button
                      variant="primary"
                      size="medium"
                      onClick={() => setEditing(!editLogo)}
                      data-testid={'cancel-editing-button'}
                      width="160px"
                    >
                      Cancel editing
                    </Button>
                  </LogoImageSection>
                )}
              </>
            }
          />
        </RequiredFeatureEnabled>
        {vaultTalkEnabled && hotlineNumbersNodes?.length > 0 && (
          <ConfigurationsSection
            subheader="Vault Talk numbers and locations"
            description="Here are all your phone numbers in your Vault Talk phonebook. This makes it easier for you to access and manage your contacts."
            right={
              <HotlineNumbers>
                <SimpleTable
                  columns={[
                    'Country',
                    <div key="language" className="language-column">
                      <span>Language</span>
                      <StyledIcon
                        data-testid={'language-more-info-icon'}
                        className="uil uil-info-circle"
                        onClick={() => setLanguageModalOpen(true)}
                        size="20px"
                      />
                    </div>,
                    'Phone Number',
                    '',
                  ]}
                  rows={hotlineNumbersNodes}
                  data-testid={'hotline-numbers'}
                />
                <Modal
                  open={languageModalOpen}
                  setOpen={setLanguageModalOpen}
                  size="medium"
                  title="Vault Talk currently supports multiple languages"
                  description={
                    <div>
                      Whilst the default language varies by country, all callers
                      to Vault Talk will be given the opportunity to report in
                      any of the supported languages. View all supported
                      languages{' '}
                      <Link
                        size="medium"
                        target="_blank"
                        href="https://help.vaultplatform.com/en/articles/10073150-vaulttalk-vault-s-phone-line"
                      >
                        here
                      </Link>
                      .
                      <br />
                      <br />
                      Vault is working hard to expand the languages we support
                      to reach and connect you with a much larger and more
                      diverse audience.
                    </div>
                  }
                  confirmButtonText="OK"
                  onConfirm={() => setLanguageModalOpen(false)}
                  data-testid={'language-more-info-modal'}
                />
              </HotlineNumbers>
            }
          />
        )}
      </CardContainer>
    </ConfigurationsContainer>
  );
};
